import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Tooltip - Re-export Radix Tooltip primitives
 *
 * Provides direct access to Radix Tooltip for app-level composition.
 * Lives in packages/ui to centralize Radix dependency ownership.
 *
 * Usage in apps/web:
 *
 * ```tsx
 * import { Tooltip } from '@protoss/ui';
 *
 * <Tooltip.Provider>
 *   <Tooltip.Root>
 *     <Tooltip.Trigger asChild>
 *       <button>Hover me</button>
 *     </Tooltip.Trigger>
 *     <Tooltip.Portal>
 *       <Tooltip.Content>
 *         Tooltip text
 *         <Tooltip.Arrow />
 *       </Tooltip.Content>
 *     </Tooltip.Portal>
 *   </Tooltip.Root>
 * </Tooltip.Provider>
 * ```
 */
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
export const Provider = TooltipPrimitive.Provider;
export const Root = TooltipPrimitive.Root;
export const Trigger = TooltipPrimitive.Trigger;
export const Portal = TooltipPrimitive.Portal;
export const Content = TooltipPrimitive.Content;
export const Arrow = TooltipPrimitive.Arrow;
/**
 * Simple - the canonical one-liner tooltip composition.
 *
 * Use this instead of the native `title` attribute (inconsistent styling and
 * delay across browsers) and instead of hand-rolling Root/Trigger/Portal/
 * Content/Arrow for plain-text tooltips. Requires a `Tooltip.Provider`
 * ancestor (mounted at the app root).
 *
 * ```tsx
 * <Tooltip.Simple label="Delete document">
 *   <button type="button" aria-label="Delete document">…</button>
 * </Tooltip.Simple>
 * ```
 *
 * Note: tooltips do not open on `disabled` elements (they fire no pointer
 * events). For a hint on a disabled control, wrap the trigger in a `<span>`.
 */
export function Simple({ label, side = 'top', align, sideOffset = 6, children }) {
    if (label == null || label === '')
        return children;
    return (_jsxs(Root, { children: [_jsx(Trigger, { asChild: true, children: children }), _jsx(Portal, { children: _jsxs(Content, { className: "ui-tooltip", side: side, align: align, sideOffset: sideOffset, children: [label, _jsx(Arrow, { className: "ui-tooltip-arrow" })] }) })] }));
}
//# sourceMappingURL=Tooltip.js.map